import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import { useButton, useI18n, useLogger } from '~Hooks';
import { getVenues } from './__queries__';

import AutoForwarder from './AutoForwarder';
import { ALL_VENUES_QUERY } from './gql';
import Logout from '~Scenes/Settings-v2/Components/Logout';
import { BlueButton, Box } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Loader from '~ComponentsImproved/Loader';
import { handyBreakpoint } from '~Styles/breakpoints';

import bLogo from '~Assets/Logos/b_logo_black.svg';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: 100vh;
  width: 100%;
  position: fixed;
  z-index: 99;
`;

const ContentSide = styled(Box)`
  animation: ${fadeIn} 0.3s ease-in-out 0.1s forwards;
  opacity: 0;
  margin-bottom: 10%;
  width: 400px;
  text-align: center;

  ${handyBreakpoint} {
    margin-bottom: auto;
  }
`;

const BLogoImg = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  filter: invert(1);
`;

const ErrorBox = styled(Box)`
  background-color: color-mix(in srgb, var(--colorUIRed) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--colorUIRed) 30%, transparent);
  border-radius: 8px;
  padding: 10px 14px;
  animation: ${fadeIn} 0.2s ease-in-out forwards;
`;

const Venues: FC = () => {
  const logger = useLogger('venues-selection');
  const { i18n } = useI18n();

  const venuesQuery = useQuery<getVenues>(ALL_VENUES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const retryButton = useButton({
    onClick: () => {
      logger.info('Retrying to fetch venues');
      venuesQuery.refetch();
    },
  });

  // A user only ever has access to a single venue nowadays, so we just grab the first active one.
  const venue = venuesQuery.data?.user?.venues[0];

  /** The query failed somehow, the user was deleted for example. */
  if (!venuesQuery.data && !venue && !venuesQuery.loading) {
    return (
      <Wrapper display="flex" alignItems="center" justifyContent="center">
        <ContentSide display="flex" flexDirection="column" alignItems="center" padding="large">
          <BLogoImg src={bLogo} alt="Baemingo" />
          <Heading level={3} color="var(--colorWhite)" top={12}>
            {i18n('App.NoPermission')}
          </Heading>
          <Paragraph level={4} color="var(--colorBlueGray200)" top={12}>
            {i18n('App.NoLocation')}
          </Paragraph>

          <Box marginTop="large">
            <Logout />
          </Box>
        </ContentSide>
      </Wrapper>
    );
  }

  if (venuesQuery.error) {
    logger.error('Error fetching venues', { errorMsg: venuesQuery.error });

    return (
      <Wrapper display="flex" alignItems="center" justifyContent="center">
        <ContentSide display="flex" flexDirection="column" alignItems="center" padding="large">
          <BLogoImg src={bLogo} alt="Baemingo" />
          <Heading level={2} color="var(--colorWhite)" top={12} bottom={16}>
            {i18n('Venues.Error')}
          </Heading>
          <ErrorBox display="flex" alignItems="center" gap="xs" marginBottom="large">
            <Paragraph level={5} color="var(--colorUIRed)">
              {i18n('Venues.Error')}
            </Paragraph>
          </ErrorBox>
          <BlueButton {...retryButton} fluid size="small">
            {i18n('General.Retry')}
          </BlueButton>
        </ContentSide>
      </Wrapper>
    );
  }

  /** User loaded but has no venue assigned, so there is nothing to forward to. */
  if (venuesQuery.data?.user && !venue) {
    logger.warn('User has no venue access', { userId: venuesQuery.data.user.id });

    return (
      <Wrapper display="flex" alignItems="center" justifyContent="center">
        <ContentSide display="flex" flexDirection="column" alignItems="center" padding="large">
          <BLogoImg src={bLogo} alt="Baemingo" />
          <Heading level={3} color="var(--colorWhite)" top={12}>
            {i18n('Venues.NoVenueAccess')}
          </Heading>
          <Paragraph level={4} color="var(--colorBlueGray200)" top={12}>
            {i18n('Venues.NoVenueAccess.Text')}
          </Paragraph>

          <Box marginTop="large">
            <Logout />
          </Box>
        </ContentSide>
      </Wrapper>
    );
  }

  return (
    <Wrapper display="flex" alignItems="center" justifyContent="center">
      <Loader color="var(--colorWhite)" />
      {venuesQuery.data?.user && <AutoForwarder venue={venue} />}
    </Wrapper>
  );
};

export default Venues;
